import { array, func, instanceOf, oneOfType, shape } from 'prop-types'
import { Fragment } from 'react'
import ResizeDetector from 'react-resize-detector'
import { Asset } from '@opallabs/cassini'
import { Size, Spacing } from '@opallabs/gem'
import {
  kuiperClassNames,
  PopoverV2,
  renderIcon,
  Tooltip,
} from '@opallabs/kuiper'
import { decorateServiceName } from '@opallabs/wormhole'

import './styles.scss'

// the width of the rendered icon
const ICON_INNER_SIZE = Size.Icon * 0.875
// the width of the icon including any padding, margin, etc
const ICON_OUTER_SIZE = ICON_INNER_SIZE + Spacing.S + Spacing.XS

/** @deprecated use ServiceIcon from @opallabs/satellite */
export const renderServiceIcon = ({
  service,
  isGhost,
  onFocusFirstItem,
  idx,
  tabIndex = 1,
}) => {
  const iconName = service.icon_name || service.system_name

  const decoratedServiceName = decorateServiceName(iconName, null, true, true)
  const classname = isGhost ? 'isGhost' : `fg-color-social-${iconName}`

  return (
    <div
      className={kuiperClassNames('service-icon', classname)}
      onFocus={() => idx === 0 && onFocusFirstItem?.()}
      tabIndex={tabIndex}
    >
      {service.asset_reference && service.asset_reference.uuid ? (
        <Asset
          assetReference={service.asset_reference}
          transforms={{
            height: ICON_INNER_SIZE * 2,
            width: ICON_INNER_SIZE * 2,
          }}
        />
      ) : (
        renderIcon(decoratedServiceName, {
          style: {
            color: service.color,
            height: ICON_INNER_SIZE,
            width: ICON_INNER_SIZE,
          },
          ariaMode: 'hidden',
        })
      )}
    </div>
  )
}

// Label for a single service icon. Callers that know which content sits behind
// an icon (e.g. the presentation moment card) tag the service with the content
// titles via `contentName`, so "Facebook" reads "Facebook - <title>".
const serviceLabel = (service) =>
  service.contentName
    ? `${service.name} - ${service.contentName}`
    : service.name

export const iconRow = (services, onFocusFirstItem) =>
  (services || []).map((service, idx) => {
    const icon = renderServiceIcon({
      service,
      isGhost: false,
      onFocusFirstItem,
      idx,
      tabIndex: 0,
    })

    const tooltipContent = (
      <div className={kuiperClassNames('momentServices-name')}>
        {serviceLabel(service)}
      </div>
    )

    return (
      <Tooltip
        key={`${service.name}${idx}`}
        trigger={icon}
        align="end"
        theme="dark"
        content={tooltipContent}
        position="bottom"
      />
    )
  })

export const ellipsisWithPopover = (remainingServices) => {
  const ellipsisIcon = (
    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    <div className={kuiperClassNames('service-icon')} tabIndex={0}>
      {renderIcon('ellipsis', {
        style: { height: ICON_INNER_SIZE, width: ICON_INNER_SIZE },
        ariaMode: 'hidden',
      })}
    </div>
    /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
  )

  const popoverContent = (
    <ul className={kuiperClassNames('serviceEllipsisPopover-content')}>
      {remainingServices.map((service, idx) => (
        <li key={`${idx}${service.name}`}>
          {renderServiceIcon({ service })}
          <p>{serviceLabel(service)}</p>
        </li>
      ))}
    </ul>
  )

  return (
    <PopoverV2
      trigger={ellipsisIcon}
      triggerEvent="hover"
      position="bottom"
      className={kuiperClassNames('serviceEllipsisPopover')}
      content={popoverContent}
    />
  )
}

export const serviceIconsWithPopover = ({
  momentCardRef,
  onFocusFirstItem,
  services,
}) => {
  let usableInlineSize = 0
  if (momentCardRef && momentCardRef.current) {
    // spacing units compensate for padding on the container
    usableInlineSize = momentCardRef.current.offsetWidth - Spacing.M
  } else {
    // fall back to old behavior of always trying to render 5 icons
    usableInlineSize = ICON_OUTER_SIZE * 5
  }

  const maximumVisibleChannelCount = Math.floor(
    usableInlineSize / ICON_OUTER_SIZE
  )
  let displayedChannelCount = maximumVisibleChannelCount
  // +1 to the length to account for add button
  if (maximumVisibleChannelCount < services.length + 1) {
    // `- 2` to compensate for the implicit ellipsis icon and add button
    displayedChannelCount -= 2
  }

  const visibleChannels = services.slice(0, displayedChannelCount)
  const visibleIcons = iconRow(visibleChannels, onFocusFirstItem) || []

  const truncatedChannels = services.slice(displayedChannelCount)
  if (truncatedChannels.length > 0) {
    return (
      <Fragment>
        {visibleIcons}
        {ellipsisWithPopover(truncatedChannels)}
      </Fragment>
    )
  }

  return visibleIcons
}

const MomentServices = ({ momentCardRef, onFocusFirstItem, services }) => {
  const serviceArray = Array.from(services || []).filter(
    (service) => service !== undefined
  )
  return (
    <div className={kuiperClassNames('momentServices')}>
      <ResizeDetector handleWidth={true}>
        {serviceIconsWithPopover({
          momentCardRef,
          onFocusFirstItem,
          services: serviceArray,
        })}
      </ResizeDetector>
    </div>
  )
}

MomentServices.propTypes = {
  // @see https://stackoverflow.com/a/51127130
  momentCardRef: oneOfType([
    func,
    shape({
      current: instanceOf(Element),
    }),
  ]),
  onFocusFirstItem: func,
  services: array,
}

export default MomentServices
